import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Building2,
    TrendingUp,
    Shield,
    MapPin,
    DollarSign,
    Calendar,
    Users,
    CheckCircle,
    ArrowRight,
    Star,
    Building,
    Target,
    Award,
    Heart,
    Sparkles,
    Eye,
    Bookmark,
    MessageCircle,
    Clock,
    BarChart3,
    PieChart,
    Home,
    Landmark,
    FileText,
    ChevronDown,
    ChevronUp
} from 'lucide-react';

import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import { usePageMetadata } from '../../hooks/usePageMetadata';

const ResidentialPropertyInvestmentNigeria = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);

    // Set page metadata and canonical URL
    usePageMetadata('residentialProperty');

    useEffect(() => {
        setIsVisible(true);
    }, []);

    // Key Features
    const features = [
        {
            icon: Building2,
            title: "Growing Demand",
            description: "High demand for quality residential properties in Nigeria's major cities"
        },
        {
            icon: TrendingUp,
            title: "Stable Appreciation",
            description: "Consistent property value appreciation with 10-18% annual returns"
        },
        {
            icon: Shield,
            title: "Secure Investment",
            description: "All properties come with proper legal documentation and title deeds"
        },
        {
            icon: Users,
            title: "Quality Tenants",
            description: "Professional tenant screening ensures reliable rental income"
        }
    ];

    // Investment Stats
    const stats = [
        { number: "10-18%", label: "Annual ROI", icon: TrendingUp, color: "text-green-600" },
        { number: "3-7 years", label: "Investment Period", icon: Calendar, color: "text-blue-600" },
        { number: "$5,000", label: "Minimum Investment", icon: DollarSign, color: "text-purple-600" },
        { number: "300+", label: "Residential Properties", icon: Building2, color: "text-orange-600" }
    ];

    // Property Types
    const propertyTypes = [
        {
            title: "Luxury Apartments",
            description: "High-end apartments in prime locations with modern amenities",
            price: "$50,000 - $200,000",
            roi: "12-18%",
            location: "Lagos, Abuja",
            image: "🏢"
        },
        {
            title: "Family Houses",
            description: "Spacious family homes perfect for long-term rentals",
            price: "$30,000 - $150,000",
            roi: "10-15%",
            location: "All Major Cities",
            image: "🏠"
        },
        {
            title: "Duplexes",
            description: "Two-story residential properties with multiple units",
            price: "$40,000 - $180,000",
            roi: "11-17%",
            location: "Lagos, Port Harcourt",
            image: "🏘️"
        },
        {
            title: "Studio Apartments",
            description: "Compact apartments ideal for young professionals",
            price: "$20,000 - $80,000",
            roi: "8-14%",
            location: "All Cities",
            image: "🏡"
        }
    ];

    // Investment Benefits
    const benefits = [
        {
            title: "Regular Rental Income",
            description: "Steady monthly rental income from reliable tenants",
            icon: DollarSign
        },
        {
            title: "Property Appreciation",
            description: "Long-term capital appreciation as property values increase",
            icon: TrendingUp
        },
        {
            title: "Portfolio Diversification",
            description: "Add residential real estate to diversify your investment portfolio",
            icon: PieChart
        },
        {
            title: "Tax Benefits",
            description: "Potential tax advantages and deductions for property investors",
            icon: FileText
        }
    ];

    // Investment Process
    const processSteps = [
        {
            step: "01",
            title: "Property Selection",
            description: "Browse our verified residential properties"
        },
        {
            step: "02",
            title: "Property Inspection",
            description: "Virtual or in-person property inspection"
        },
        {
            step: "03",
            title: "Legal Documentation",
            description: "Complete all legal requirements and contracts"
        },
        {
            step: "04",
            title: "Tenant Management",
            description: "Professional tenant screening and management"
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            
            {/* Hero Section */}
            <section className="relative pt-20 pb-16 bg-white">
                <div className="container mx-auto px-6 relative z-10">
                    <div className={`text-center max-w-4xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                            <Building2 className="w-4 h-4" />
                            Residential Property Investment Nigeria
                        </div>
                        
                        <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                            Invest in Nigeria's
                            <span className="text-blue-600"> Residential Real Estate</span>
                        </h1>
                        
                        <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                            Discover profitable residential property investment opportunities in Nigeria's fastest-growing cities. 
                            From luxury apartments to family homes, invest with confidence and earn stable returns.
                        </p>
                        
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-orange-500/25 transition-all duration-300 hover:-translate-y-1">
                                Start Investing
                                <ArrowRight className="w-5 h-5" />
                            </button>
                            <button className="inline-flex items-center gap-2 bg-white text-gray-700 px-8 py-4 rounded-xl font-semibold border border-gray-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                                <Eye className="w-5 h-5" />
                                View Properties
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Why Invest in Residential Real Estate?
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Residential properties offer stable returns, regular income, and long-term appreciation potential.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-6">
                                    <feature.icon className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Property Types Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Available Residential Properties
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Choose from our diverse portfolio of residential properties across Nigeria's major cities.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {propertyTypes.map((property, index) => (
                            <div
                                key={index}
                                className={`bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden group border border-gray-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="h-48 bg-blue-50 flex items-center justify-center text-6xl">
                                    {property.image}
                                </div>
                                
                                <div className="p-6">
                                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors duration-300">
                                        {property.title}
                                    </h3>
                                    <p className="text-gray-600 mb-4 leading-relaxed">
                                        {property.description}
                                    </p>
                                    
                                    <div className="space-y-2 mb-6">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Price Range:</span>
                                            <span className="font-semibold text-gray-900">{property.price}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Expected ROI:</span>
                                            <span className="font-semibold text-green-600">{property.roi}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Locations:</span>
                                            <span className="font-semibold text-orange-600">{property.location}</span>
                                        </div>
                                    </div>
                                    
                                    <button className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                                        View Details
                                        <ArrowRight className="w-4 h-4 inline ml-2" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Residential Investment Benefits
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Residential real estate offers unique advantages for investors seeking stable, long-term returns.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {benefits.map((benefit, index) => (
                            <div
                                key={index}
                                className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-6">
                                    <benefit.icon className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Investment Process Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Investment Process
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Our streamlined process makes residential real estate investment simple and secure.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {processSteps.map((step, index) => (
                            <div
                                key={index}
                                className={`text-center transition-all duration-1000 delay-${index * 100} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                            >
                                <div className="relative mb-8">
                                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-600 to-blue-600 rounded-full text-white text-2xl font-bold mb-4">
                                        {step.step}
                                    </div>
                                    {index < processSteps.length - 1 && (
                                        <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-blue-600 -translate-x-1/2"></div>
                                    )}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-blue-600">
                <div className="container mx-auto px-6 text-center">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                            Ready to Invest in Residential Real Estate?
                        </h2>
                        <p className="text-xl text-orange-100 mb-8">
                            Join successful investors who have built wealth through Nigerian residential real estate investments.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button className="inline-flex items-center gap-2 bg-white text-orange-600 px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                                Start Your Investment
                                <ArrowRight className="w-5 h-5" />
                            </button>
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 bg-transparent text-white px-8 py-4 rounded-xl font-semibold border-2 border-white hover:bg-white hover:text-orange-600 transition-all duration-300 hover:-translate-y-1"
                            >
                                <MessageCircle className="w-5 h-5" />
                                Contact Us
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Learn More About Residential Property Investment in Nigeria Section */}
            <section className="bg-white py-12 px-4 border-t border-gray-200">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-8">
                        <button
                            onClick={() => setIsLearnMoreOpen(!isLearnMoreOpen)}
                            className="flex items-center justify-center gap-2 mx-auto bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors duration-300 border border-gray-300"
                            aria-expanded={isLearnMoreOpen}
                            aria-controls="residential-content"
                        >
                            <span className="font-medium">
                                {isLearnMoreOpen ? 'Hide Learn More About Residential Property Investment in Nigeria' : 'Learn More About Residential Property Investment in Nigeria'}
                            </span>
                            {isLearnMoreOpen ? (
                                <ChevronUp className="w-4 h-4 text-gray-600" />
                            ) : (
                                <ChevronDown className="w-4 h-4 text-gray-600" />
                            )}
                        </button>
                    </div>

                    <div
                        id="residential-content"
                        className={`overflow-hidden transition-all duration-500 ease-in-out ${isLearnMoreOpen ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'
                            }`}
                    >
                        <article className="prose prose-lg max-w-none">
                            {/* Main Content Section */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                                    Introduction: Investment in Residential Property in Nigeria
                                </h2>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Nigeria, Africa's largest economy with an estimated 220 million people, is urbanizing rapidly and developing its middle class. As a result, residential property investment in Nigeria has become one of the most lucrative opportunities for both local and diaspora investors.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    With a shortfall of more than 17 million houses, the market for sustainable and affordable housing is huge. Lagos, Abuja, and Port Harcourt are expanding quickly, while modern estates, gated communities, and high-rise apartments are springing up in new cities.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    For investors, this translates into a proven mix of: Steady rental income from tenants. Capital appreciation of land and property values over time, and. Nation-building impact through job creation and infrastructure development.
                                </p>
                            </div>

                            {/* Section 2 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">The Increasing Demand for Residential Real Estate in Nigeria</h2>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    A home is more than just a property — it represents stability, family security, and cultural identity. For many Nigerians abroad, owning a home back home is both an emotional and financial goal.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Historically, many entrusted family members to handle property purchases. Too often, this led to fraud or unfinished projects. Today, digital platforms like Diasporawise have solved this problem by providing safe, transparent, and structured ways to invest strategically in Nigeria's booming housing market.
                                </p>
                            </div>

                            {/* Section 3 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Is it Worth Investing in Residential Property in Nigeria?</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">1. Huge Housing Demand</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Nigeria's population is expected to surpass 250 million by 2050. Urban migration is fueling demand for homes in Lagos, Abuja, and Port Harcourt, ensuring consistent rental demand.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">2. Attractive Rental Yields</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Residential real estate in Nigeria can deliver annual returns of 6%–10%, with short-let apartments in Lagos achieving even higher returns from business travelers and tourists.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">3. Long-Term Capital Growth</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Properties in fast-growing urban areas consistently appreciate. For example, land banking in new suburbs of Lagos or Abuja often provides solid long-term returns.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">4. Government Support for Diaspora Investment</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    The Nigerian government has introduced housing policies, mortgage products, and incentives that encourage diaspora participation in residential property projects.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">5. Safe Digital Platforms</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Platforms like Diasporawise allow investors to purchase property online, verify ownership and titles, and track progress digitally — making investment more transparent and secure than ever.
                                </p>
                            </div>

                            {/* Section 4 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Forms of Investment in Residential Property in Nigeria</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">1. Gated Communities</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Gated estates provide security, modern infrastructure, and attract strong tenant demand. They are highly popular with middle-class families and professionals.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">2. Apartment Complexes</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    High-rise apartments in Lagos and Abuja are ideal for both long-term rentals and short-let opportunities, driven by professionals and expatriates.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">3. Short-Let Apartments</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Tourism, business travel, and platforms like Airbnb have made short-let apartments one of the fastest-growing niches. They typically generate higher daily returns than regular rentals.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">4. Affordable Housing Projects</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Affordable housing targets Nigeria's growing population in need of cost-effective homes. Many government-backed schemes encourage private sector involvement in this space.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">5. Luxury Homes & Duplexes</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    High-net-worth individuals in Lagos and Abuja are driving demand for luxury homes and duplexes, which offer prestige and strong appreciation potential.
                                </p>
                            </div>

                            {/* Section 5 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Advantages of Investing in Residential Property via Diasporawise</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Security & Compliance</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Verified ownership, titles, and approvals.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Transparency</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Regular digital updates and progress reports.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Accessibility</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Start small with fractional property investments.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Nation-Building</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Contribute to closing Nigeria's housing deficit.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Cultural Connection</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Maintain ties with your homeland through ownership.
                                </p>
                            </div>

                            {/* Section 6 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">How to Start Investing in Residential Property in Nigeria</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Know Your Goals</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Decide whether you want rental income, capital growth, or both.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Choose a Trusted Platform</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Use transparent, secure sites like Diasporawise.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Select Property Type</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Options include estates, apartments, short-let, or affordable housing.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Verify Documentation</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Check land titles and permits digitally.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Make Secure Payments</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Always transact via official online payment channels.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Monitor Your Investment</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Use dashboards and reports for oversight.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Adopt a Long-Term Mindset</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Property is a wealth-building asset over time.
                                </p>
                            </div>

                            {/* Section 7 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Challenges and Solutions in Residential Property Investment</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Fraud & Title Issues</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Stick to verified platforms and conduct proper legal checks.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Unfinished Projects</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Work only with reputable developers with proven track records.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Property Management Issues</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Hire professional management companies for rentals.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Foreign Exchange Risks</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Plan for fluctuations in exchange rates when transferring funds from abroad.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    By anticipating these challenges, investors can protect their investments and ensure consistent returns.
                                </p>
                            </div>

                            {/* Conclusion */}
                            <div className="mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Conclusion: Building Wealth and Home through Residential Real Estate</h2>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Investing in residential property in Nigeria is more than just a way to build wealth — it's a chance to create a legacy, bridge the housing gap, and strengthen cultural ties.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    For diaspora Nigerians, this means not only steady rental income and capital appreciation but also establishing a long-term connection to home.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    With platforms like Diasporawise, investors can confidently explore gated communities, apartments, short-let rentals, and affordable housing projects — all while balancing safety, transparency, and growth.
                                </p>
                                <p className="text-gray-700 leading-relaxed">
                                    Whether you're starting with fractional investments or expanding into full ownership, residential real estate remains one of the most secure and profitable opportunities in Nigeria today.
                                </p>
                            </div>
                        </article>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ResidentialPropertyInvestmentNigeria;
